import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { GAMES } from '@/common/utils/helpers';
import {
  GameInstance,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { GridLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/GridLayoutGroup';
import { ScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroup';
import { getAnchorPlugin } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import MasteriesPanel from '@/game/prefabs/Masteries/MasteriesPanel';
import MinigameMasteryUI from '@/game/prefabs/Masteries/MinigameMasteryUI';

export class MasteriesPanelController {
  constructor(private masteriesPanel: MasteriesPanel) {
    EventsManager.registerEventOn(
      masteriesPanel.scene,
      GAME_EVENTS.TILEMAP_LEVEL_INITIALIZED,
      this.init,
      this,
    );
    EventsManager.registerEventOn(
      masteriesPanel.scene,
      GAME_EVENTS.MASTERIES_PANEL_OPENED,
      this.open,
      this,
    );
    EventsManager.registerEventOn(
      masteriesPanel.scene,
      GAME_EVENTS.NODE_COMPLETED,
      this.updateMasteries,
      this,
    );
  }

  private gridLayoutGroup?: GridLayoutGroup;
  private gridLayoutGroup_m?: GridLayoutGroup;
  private masteries: MinigameMasteryUI[] = [];
  private masteries_m: MinigameMasteryUI[] = [];

  getResponsiveElements(key: keyof MasteriesPanel) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromContainer(
      this.masteriesPanel,
      key,
    );
  }

  open() {
    this.masteriesPanel.setVisible(true);
    PhaserAudioManager.playSFX(this.masteriesPanel.scene, 'npc_dialogue_open');
  }

  init() {
    const { masteriesPanel } = this;

    // Init minigames
    const sLevel = getStudent().level;
    const { customLevel } = GameInstance.instance;
    const shouldShowCustomLevel =
      GameInstance.instance.getShouldPlayCustomLevel();
    let minigames;
    if (shouldShowCustomLevel && customLevel) {
      minigames = GAMES.filter((g) => customLevel[g].enabled);
    } else {
      minigames = scopeAndSequence.levels.find(
        (d) => d.level === sLevel,
      )?.games;
    }
    if (!minigames) {
      console.warn('no minigames found for level: ', sLevel);
      return;
    }

    // Generate masteries.
    for (var i = 0; i < minigames.length; i++) {
      // Desktop mastery
      const newMastery = new MinigameMasteryUI(masteriesPanel.scene, 0, 0);
      newMastery.setMinigame(minigames[i]);
      this.masteries.push(newMastery);

      // Mobile mastery.
      const newMobileMastery = new MinigameMasteryUI(
        masteriesPanel.scene,
        0,
        0,
      );
      newMobileMastery.setMinigame(minigames[i]);
      this.masteries_m.push(newMobileMastery);
    }

    // Desktop masteries container setup.
    this.gridLayoutGroup = new GridLayoutGroup({
      scene: masteriesPanel.scene,
      elements: this.masteries,
      columns: 2,
      vPadding: 76,
      hPadding: 292,
      alignment: 'LEFT',
    });
    masteriesPanel.masteriesContainer.add(this.gridLayoutGroup);
    this.gridLayoutGroup.setPosition(-260, -80);

    // Mobile masteries container setup.
    this.gridLayoutGroup_m = new GridLayoutGroup({
      scene: masteriesPanel.scene,
      elements: this.masteries_m,
      columns: 2,
      vPadding: 76,
      hPadding: 292,
      alignment: 'LEFT',
    });
    masteriesPanel.masteriesContainer_m.add(this.gridLayoutGroup_m);
    this.gridLayoutGroup_m.setPosition(-260, -80);

    this.getResponsiveElements('levelText').forEach(
      (levelText: Phaser.GameObjects.BitmapText) => {
        levelText.text =
          shouldShowCustomLevel && customLevel
            ? customLevel.description || 'Custom Level'
            : `Level ${getStudent().level}`;
      },
    );

    // Init close button.
    this.getResponsiveElements('closeButton').forEach(
      (closeButton: HUDButton) => {
        closeButton.setSubmissionCallback(() => {
          masteriesPanel.setVisible(false);
        });
      },
    );

    // Init scroll group for mobile view.
    new ScrollGroup({
      scene: masteriesPanel.scene,
      content: masteriesPanel.masteriesContainerOffset_m,
      scrollElement: masteriesPanel.scrollGroupBacking,
      scrollBacking: masteriesPanel.scrollBarBacking_m,
      scrollHandle: masteriesPanel.scrollBarHandle_m,
      scrollSpeedX: 0,
    });

    masteriesPanel.setVisible(false);

    this.updateMasteries();

    this.initializeResponsiveness();
  }

  updateMasteries() {
    if (!this.masteries.length) {
      return;
    }

    const sLevel = getStudent().level;
    const { customLevel } = GameInstance.instance;
    const shouldShowCustomLevel =
      GameInstance.instance.getShouldPlayCustomLevel();
    let minigames;
    if (shouldShowCustomLevel && customLevel) {
      minigames = GAMES.filter((g) => customLevel[g].enabled);
    } else {
      minigames = scopeAndSequence.levels.find(
        (d) => d.level === sLevel,
      )?.games;
    }

    if (!minigames) {
      console.warn('no minigames found for level: ', sLevel);
      return;
    }
    // Configure minigame mastery UI elements.
    for (let i = 0; i < minigames.length; i++) {
      this.masteries[i].setMinigame(minigames[i]);
      this.masteries_m[i].setMinigame(minigames[i]);
    }
  }

  initializeResponsiveness() {
    const { masteriesPanel } = this;

    // Breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: masteriesPanel.scene,
      desktopView: masteriesPanel.desktopView,
      mobileView: masteriesPanel.mobileView,
    });

    // Responsiveness.
    const anchor = getAnchorPlugin(masteriesPanel.scene);
    anchor.add(masteriesPanel.mainContainer, {
      centerX: '50%',
      centerY: '50%',
    });

    anchor.add(masteriesPanel.mainContainer_m, {
      centerX: '50%',
      centerY: '50%',
    });
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      masteriesPanel.mainContainer,
    );
  }
}
